<template>
  <div class="row">
    <div class="col-sm-12 col-xl-6">
      <div class="card">
        <div class="card-header pb-0">
          <h4>{{ title }}</h4>
          <span>{{ subtitle }}</span>
        </div>
        <div class="card-body">
          <form class="theme-form" @submit.prevent="onSubmit">
            <div class="mb-3">
              <label class="col-form-label pt-0" for="email">Correo electrónico</label>
              <input
                id="email"
                v-model="form.email"
                type="email"
                class="form-control"
                placeholder="cliente@ejemplo.com"
                required
              />
            </div>
            <div class="mb-3">
              <label class="col-form-label pt-0" for="nombre">Nombre</label>
              <input
                id="nombre"
                v-model="form.nombre"
                type="text"
                class="form-control"
                :placeholder="namePlaceholder"
                required
              />
            </div>
            <div class="mb-3">
              <label class="col-form-label pt-0" for="phone">Teléfono</label>
              <input
                id="phone"
                v-model="form.phone"
                type="tel"
                class="form-control"
                placeholder="Ej. 5512345678"
                pattern="^[0-9+\\-()\\s]{10,20}$"
                required
              />
            </div>
            <div class="mb-3">
              <label class="col-form-label pt-0" for="password">Contraseña</label>
              <input
                id="password"
                v-model="form.password"
                type="password"
                class="form-control"
                placeholder="Mínimo 6 caracteres"
                required
                minlength="6"
              />
            </div>

            <template v-if="isTraveler">
              <hr />
              <h6 class="mb-3">Datos fiscales y de identidad</h6>

              <div class="mb-3">
                <label class="col-form-label pt-0" for="identityDocumentUrl">Documento de identidad (URL opcional)</label>
                <input
                  id="identityDocumentUrl"
                  v-model="form.identityDocumentUrl"
                  type="url"
                  class="form-control"
                  placeholder="https://..."
                />
              </div>
              <div class="mb-3">
                <label class="col-form-label pt-0" for="dniFrontUrl">DNI Frente (URL opcional)</label>
                <input
                  id="dniFrontUrl"
                  v-model="form.dniFrontUrl"
                  type="url"
                  class="form-control"
                  placeholder="https://..."
                />
              </div>
              <div class="mb-3">
                <label class="col-form-label pt-0" for="dniBackUrl">DNI Reverso (URL opcional)</label>
                <input
                  id="dniBackUrl"
                  v-model="form.dniBackUrl"
                  type="url"
                  class="form-control"
                  placeholder="https://..."
                />
              </div>
              <div class="mb-3">
                <label class="col-form-label pt-0" for="rfc">RFC</label>
                <input
                  id="rfc"
                  v-model="form.rfc"
                  type="text"
                  class="form-control"
                  placeholder="Ej. XAXX010101000"
                  maxlength="20"
                />
              </div>
              <div class="mb-3">
                <label class="col-form-label pt-0" for="razonSocial">Razón Social</label>
                <input
                  id="razonSocial"
                  v-model="form.razonSocial"
                  type="text"
                  class="form-control"
                  placeholder="Razón social"
                />
              </div>
              <div class="mb-3">
                <label class="col-form-label pt-0" for="direccion">Dirección</label>
                <input
                  id="direccion"
                  v-model="form.direccion"
                  type="text"
                  class="form-control"
                  placeholder="Dirección"
                />
              </div>
              <div class="mb-3">
                <label class="col-form-label pt-0" for="codigoPostal">Código Postal</label>
                <input
                  id="codigoPostal"
                  v-model="form.codigoPostal"
                  type="text"
                  class="form-control"
                  placeholder="Ej. 01000"
                  pattern="^[0-9]{5}$"
                />
              </div>
            </template>
            <div v-if="errorMsg" class="alert alert-danger" role="alert">
              {{ errorMsg }}
            </div>
            <div v-if="successMsg" class="alert alert-success" role="alert">
              {{ successMsg }}
            </div>
            <div class="card-footer text-end px-0">
              <button type="submit" class="btn btn-primary" :disabled="loading">
                {{ loading ? 'Guardando...' : submitLabel }}
              </button>
              <button type="button" class="btn btn-secondary ms-2" @click="resetForm">
                Limpiar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import { clientsApi } from '@/api/clients';
import { normalizeApiError } from '@/utils/error-handler';

export default {
  name: 'ClienteForm',
  props: {
    role: { type: String, required: true },
    title: { type: String, required: true },
    subtitle: { type: String, required: true },
    submitLabel: { type: String, default: 'Dar de alta' },
    namePlaceholder: { type: String, default: 'Nombre completo' },
    successTitle: { type: String, default: 'Registro creado' },
  },
  data() {
    return {
      form: {
        role: this.role,
        email: '',
        nombre: '',
        phone: '',
        password: '',
        identityDocumentUrl: '',
        dniFrontUrl: '',
        dniBackUrl: '',
        rfc: '',
        razonSocial: '',
        direccion: '',
        codigoPostal: '',
      },
      loading: false,
      errorMsg: '',
      successMsg: '',
    };
  },
  computed: {
    isTraveler() {
      return this.role === 'CONDUCTOR_VIAJERO';
    },
  },
  watch: {
    role: {
      immediate: true,
      handler(role) {
        this.form.role = role;
      },
    },
  },
  methods: {
    buildPayload() {
      const payload = {
        role: this.role,
        email: String(this.form.email || '').trim(),
        nombre: String(this.form.nombre || '').trim(),
        phone: String(this.form.phone || '').trim(),
        password: String(this.form.password || ''),
      };

      const optionalFields = {
        identityDocumentUrl: this.form.identityDocumentUrl,
        dniFrontUrl: this.form.dniFrontUrl,
        dniBackUrl: this.form.dniBackUrl,
        rfc: this.form.rfc,
        razonSocial: this.form.razonSocial,
        direccion: this.form.direccion,
        codigoPostal: this.form.codigoPostal,
      };

      Object.entries(optionalFields).forEach(([key, value]) => {
        const cleaned = String(value || '').trim();
        if (cleaned) payload[key] = cleaned;
      });

      return payload;
    },
    async onSubmit() {
      this.errorMsg = '';
      this.successMsg = '';
      this.loading = true;
      try {
        const resp = await clientsApi.create(this.buildPayload());
        const createdClient = resp?.data || {};
        const phoneProvided = this.form.phone?.trim();
        const phoneCodeSent = Boolean(createdClient.phoneCodeSent);

        if (phoneProvided) {
          this.successMsg = phoneCodeSent
            ? 'Registro exitoso. Se envió correo de verificación y código SMS.'
            : 'Registro exitoso. Se envió correo de verificación, pero falló el envío del SMS.';
        } else {
          this.successMsg = 'Registro exitoso. Se envió correo de verificación.';
        }

        this.resetForm();

        await Swal.fire({
          icon: 'success',
          title: this.successTitle,
          text: this.successMsg,
          confirmButtonColor: '#FBC903',
        });
      } catch (e) {
        const message = normalizeApiError(e, 'Error al registrar. Intente de nuevo.');
        this.errorMsg = message;
        await Swal.fire({
          icon: 'error',
          title: 'Error al registrar',
          text: message,
          confirmButtonColor: '#FBC903',
        });
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.form = {
        role: this.role,
        email: '',
        nombre: '',
        phone: '',
        password: '',
        identityDocumentUrl: '',
        dniFrontUrl: '',
        dniBackUrl: '',
        rfc: '',
        razonSocial: '',
        direccion: '',
        codigoPostal: '',
      };
      this.errorMsg = '';
      this.successMsg = '';
    },
  },
};
</script>

